import AuthRoute, { AuthRouteConditions } from '@/components/app/AuthRoute';
import { BygrModule, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import ProfileInvite from '@/components/profile/ProfileInvite';
import ProfileUserManagement from '@/components/profile/ProfileUserManagement';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Switch, useRouteMatch } from 'react-router-dom';
import LandingPage from './components/LandingPage';
// import NavBar from './components/Sidebar';
import Budget from './components/Budget';
import BuyerDashboard from './components/BuyerDashboard';
import { BuyerDocuments } from './components/BuyerDocuments';
import UnitSettings from '@/components/user-settings/UnitSettings';
import UserSettings from '@/components/user-settings/UserSettings';
import { loadBuyerChangeRequests } from '@/state/buyer/changeRequests/creator';
import { loadBuyerCommentThreads } from '@/state/buyer/commentThreads/creator';
import { loadBuyerDocumentTypes } from '@/state/buyer/document-types/creator';
import { loadBuyerMeetings } from '@/state/buyer/meetings/creator';
import { setBuyerPermissions } from '@/state/buyer/permissions/actions';
import { loadBuyerPermissions } from '@/state/buyer/permissions/creator';
import { loadProjectData } from '@/state/buyer/project-data/creator';
import { IState } from '@/state/IState';
import { ProjectFeature, ProjectFeatureMode } from '@/types';
import { getModuleUrl } from '@/utils/returnUrl';
import BuyerListener from './BuyerListener';
import BookMeeting from './components/BookMeeting';
import BuyerChangeRequests from './components/BuyerChangeRequests/BuyerChangeRequests';
import BuyerChangeRequestSubmit from './components/BuyerChangeRequests/BuyerChangeRequestSubmit';
import { BuyerUnitChat } from './components/BuyerChat';
import BuyerSidebar from './components/BuyerSidebar';
import { BuyerWhiteLabelProvider } from './components/BuyerWhiteLabelProvider';
import { helpKitSwitcher } from '@ygtechnologies/bygr-frontend-tools';
import { useAppDispatch } from '@/utils/hooks';
import { BuyerOptionConfigurator, OptionConfigurator } from '../components/OptionConfigurator';
// import { elvelyTheme } from './components/elvelyTheme';

const profileAdminOnly: AuthRouteConditions = { profile: 'isAdministrator' };
type BuyerRoutingProps = {
    residentialUnitId?: number;
};

const BuyerRouting: React.FC<BuyerRoutingProps> = ({ residentialUnitId }) => {
    const dispatch = useAppDispatch();
    const projectData = useSelector((state: IState) => state.buyer.projectData);
    const profileId = useSelector((state: IState) => state.global.activeProfile!.id);
    const permissions = useSelector((state: IState) => state.buyer.permissions);
    const match = useRouteMatch();
    var basePath = match!.path === '/' ? '' : match!.path;
    useEffect(() => {
        helpKitSwitcher('rucoriaenduser');
    }, []);

    useEffect(() => {
        if (residentialUnitId) {
            dispatch(
                setBuyerPermissions({ residentialUnitId, canModify: false, impersonating: true, isInReadOnlyMode: false })
            );
        } else if (profileId) {
            dispatch(loadBuyerPermissions());
        }

        return function cleanup() {
            setBuyerPermissions(null);
        };
    }, [residentialUnitId, profileId, dispatch]);

    useEffect(() => {
        if (permissions) {
            dispatch(loadProjectData());
            dispatch(loadBuyerCommentThreads());
            dispatch(loadBuyerChangeRequests());
            dispatch(loadBuyerMeetings());
        }
    }, [permissions, dispatch]);

    useEffect(() => {
        loadBuyerDocumentTypes(dispatch);
    }, [dispatch]);

    const canAccessFeature = useCallback(
        (feature: ProjectFeature) => {
            if (!permissions || !projectData || !projectData.features) {
                return false;
            }

            if (permissions.impersonating) {
                return true;
            }

            return projectData.features.some((x) => x.feature === feature && x.mode === ProjectFeatureMode.Enabled);
        },
        [projectData, permissions]
    );

    if (!projectData || !permissions) {
        return <LoadingIndicator />;
    }

    if (
        !permissions.impersonating &&
        !projectData.buyerModules.some((x) => x === BygrModule.ChangeManagement) &&
        projectData.buyerModules.some((x) => x === BygrModule.Aftermarket)
    ) {
        window.location.href = getModuleUrl(BygrModule.Aftermarket, window.APP_CONFIG);
        return <LoadingIndicator />;
    }

    const shouldRedirect = !sessionStorage.getItem('shouldRedirectAM');
    if (projectData.buyerModules.some((x) => x === BygrModule.Aftermarket) && shouldRedirect && !permissions.impersonating) {
        sessionStorage.setItem('shouldRedirectAM', 'false');
        window.location.href = getModuleUrl(BygrModule.Aftermarket, window.APP_CONFIG);
        return <LoadingIndicator />;
    }

    const initialPagePriority = [
        {
            feature: ProjectFeature.LandingPage,
            route: `${basePath}/landing`,
        },
        {
            feature: ProjectFeature.Dashboard,
            route: `${basePath}/dashboard`,
        },
        {
            feature: ProjectFeature.Options,
            route: `${basePath}/options`,
        },
        {
            feature: ProjectFeature.Budget,
            route: `${basePath}/budget`,
        },
        {
            feature: ProjectFeature.ChangeRequests,
            route: `${basePath}/change-requests`,
        },
        {
            feature: ProjectFeature.Documents,
            route: `${basePath}/files`,
        },
        {
            feature: ProjectFeature.Chat,
            route: `${basePath}/chat`,
        },
    ];

    const initialFeature = initialPagePriority.filter((x) => canAccessFeature(x.feature))[0];

    return (
        <BuyerWhiteLabelProvider>
            <BuyerListener />
            <BuyerSidebar />
            <Switch>
                <AuthRoute exact path={`${basePath}/user-settings`} component={UserSettings} />
                <AuthRoute exact path={`${basePath}/profile-settings`} component={UnitSettings} />
                <AuthRoute
                    exact
                    path={`${basePath}/profile-users`}
                    component={ProfileUserManagement}
                    conditions={profileAdminOnly}
                />
                <AuthRoute
                    exact
                    path={`${basePath}/profile-users/invite`}
                    component={ProfileInvite}
                    conditions={profileAdminOnly}
                />

                {canAccessFeature(ProjectFeature.LandingPage) && (
                    <AuthRoute
                        exact
                        path={`${basePath}/landing/:tab(news|contacts|details|floor-plan)?`}
                        component={LandingPage}
                    />
                )}
                {canAccessFeature(ProjectFeature.Dashboard) && (
                    <AuthRoute exact path={`${basePath}/dashboard`} component={BuyerDashboard} />
                )}
                {canAccessFeature(ProjectFeature.Documents) && (
                    <AuthRoute exact path={`${basePath}/files/:versionId?`} component={BuyerDocuments} />
                )}
                {canAccessFeature(ProjectFeature.Chat) && (
                    <AuthRoute exact path={`${basePath}/chat/:activeChatId?`} component={BuyerUnitChat} />
                )}

                {canAccessFeature(ProjectFeature.Options) && (
                    <AuthRoute
                        path={`${basePath}/options/`}
                        component={BuyerOptionConfigurator}
                        extraProps={{ residentialUnitId: permissions.residentialUnitId }}
                    />
                )}
                {canAccessFeature(ProjectFeature.Budget) && (
                    <AuthRoute exact path={`${basePath}/budget`} component={Budget} />
                )}

                {canAccessFeature(ProjectFeature.ChangeRequests) && [
                    <AuthRoute key="1" exact path={`${basePath}/change-requests`} component={BuyerChangeRequests} />,
                    <AuthRoute
                        key="2"
                        exact
                        path={`${basePath}/change-requests/:changeRequestId/offers/:buyerOfferId/submit`}
                        component={BuyerChangeRequestSubmit}
                    />,
                    <AuthRoute
                        key="3"
                        exact
                        path={`${basePath}/change-requests/:activeChangeRequestId?/:tabId?`}
                        component={BuyerChangeRequests}
                    />,
                ]}
                <AuthRoute exact path={`${basePath}/book-meeting`} component={BookMeeting} />

                {initialFeature && <Redirect to={initialFeature.route} />}
                {/* <Redirect to={`${basePath}/landing`} /> */}

                {/* <Route component={NotFound} /> */}
            </Switch>
        </BuyerWhiteLabelProvider>
    );
};

export default BuyerRouting;
